'use memo';

import {
  AgxBodyText,
  AgxButton,
  AgxColumn,
  AgxHeader,
  AgxRow,
  AgxSlideUpModal,
  AgxSparkleForm,
  EnvelopeSubmissionType,
  FormConfigType,
  Images,
  MarketingPackageStage,
  SPECIAL_CHARACTERS,
} from '@urbanx/agx-ui-components';
import dayjs from 'dayjs';
import { completeMarketingPackageStep } from 'Api/Marketing/marketingApi';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useFormLoader } from 'hooks/useFormLoader';
import { useContext, useState } from 'react';
import { setAndShowErrorToast } from 'store/config';
import { formatCurrency } from 'utils/formatNumber';
import { Breakpoints, ScreenSize } from 'utils/screen';
import FormDetailsHeader from '../../Common/FormDetailsHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getMarketingPackageTotal } from 'utils/marketingPackageUtil';
import { CampaignDetailModel } from '@urbanx/agx.campaigns.client';
import { MarketingPackageProjection } from '@urbanx/agx.marketing.client';

interface Props {
  onBack: () => void;
  campaign: CampaignDetailModel;
  marketingPackage: MarketingPackageProjection;
  addressLineOne: string;
  addressLineTwo: string;
}

const ResumeMarketingPackage = (props: Props) => {
  const { onBack, campaign, marketingPackage, addressLineOne, addressLineTwo } =
    props;
  const { campaignId } = campaign;

  const lastUpdatedOn = `Last updated ${dayjs(marketingPackage.lastUpdated).format('DD/MM/YY')}`;

  const dispatch = useAppDispatch();
  const { getAuthToken } = useContext(AuthContext);
  const { loadForm, loadFormWithConfig } = useFormLoader();

  const [showConfirmationModel, setShowConfirmationModel] = useState(false);
  const [disableButtons, setDisableButtons] = useState(false);
  const isMobile = ScreenSize() === Breakpoints.Mobile;

  const marketingPackageTotal = getMarketingPackageTotal(marketingPackage);

  const resumeMarketingPackage = async () => {
    try {
      setDisableButtons(true);

      await loadForm(campaignId ?? '', marketingPackage.id ?? '', FormConfigType.Marketing);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to resume Marketing'));
    }

    setDisableButtons(false);
  };

  const beginMarketingPackageSubmission = async () => {
    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) {
        dispatch(setAndShowErrorToast('Failed to start Marketing submission'));
        setDisableButtons(false);
        return;
      }

      const newForm = await completeMarketingPackageStep(
        authToken,
        campaignId ?? '',
        marketingPackage.id ?? '',
        true
      );

      if (!newForm) {
        dispatch(setAndShowErrorToast('Failed to start Marketing submission'));
        setDisableButtons(false);
        return;
      }

      await loadFormWithConfig(newForm);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to start Marketing submission'));
    }

    setDisableButtons(false);
  };

  const confirmationDialogBody = (
    <AgxColumn veryLargeGap extraClasses={'confirmationModalStyle'}>
      <Images.AlertCircleOutline />
      <AgxHeader size={3}>Starting a submission?</AgxHeader>
      <AgxBodyText medium>
        You will not be able to edit the Marketing Package once you continue.
      </AgxBodyText>
      <AgxBodyText medium>Continue to submission?</AgxBodyText>
      <div className={'confirmationButtonContainerStyle'}>
        <AgxButton
          id="startsubmission"
          large
          primary
          text="Yes, start submission"
          onClick={beginMarketingPackageSubmission}
          disabled={disableButtons}
        />
        <AgxButton
          id="cancel"
          hollow
          large
          text="No, go back"
          onClick={() => setShowConfirmationModel(false)}
          disabled={disableButtons}
        />
      </div>
    </AgxColumn>
  );

  return (
    <>
      <FormDetailsHeader
        id={'resume-marketing-header'}
        formName="Marketing"
        detail={lastUpdatedOn}
        onBack={onBack}
      />
      <AgxColumn centered veryLargeGap extraClasses="resumeMarketingDetails">
        <AgxHeader size={2} centered>
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxHeader>
        <AgxBodyText medium centered>
          {marketingPackage.packageTemplateName} {SPECIAL_CHARACTERS.BULLET}{' '}
          {formatCurrency(marketingPackageTotal, 0)}
        </AgxBodyText>
        <AgxColumn mediumGap centered>
          {!marketingPackage.document && (
            <AgxColumn smallGap centered>
              <AgxBodyText medium centered>
                There are more fields to fill,
              </AgxBodyText>
              <AgxBodyText medium centered>
                let's continue from where you left off
              </AgxBodyText>
            </AgxColumn>
          )}
        </AgxColumn>
        <AgxSparkleForm
          width={isMobile ? 150 : 200}
          height={isMobile ? 150 : 200}
        />
      </AgxColumn>
      <AgxRow fill justifyCenter>
        <AgxColumn mediumGap>
          <AgxButton
            id="btn-resume"
            hollow={!isMobile}
            primary={isMobile}
            wide={isMobile}
            large
            text={`Resume ${
              isMobile
                ? ''
                : marketingPackage.stage?.value ===
                    MarketingPackageStage.SubmissionIncomplete
                  ? 'Submission'
                  : 'Marketing'
            }`}
            onClick={resumeMarketingPackage}
            disabled={disableButtons}
          />
          {marketingPackage.envelopeSubmission?.type !==
            EnvelopeSubmissionType.Manual &&
            marketingPackage.document && (
              <AgxButton
                id="btn-startSubmission"
                primary
                wide={isMobile}
                large
                disabled={disableButtons}
                text="Start Submission"
                onClick={() => setShowConfirmationModel(true)}
              />
            )}
        </AgxColumn>
      </AgxRow>
      {showConfirmationModel && (
        <AgxSlideUpModal
          closeButton
          content={confirmationDialogBody}
          desktop={!isMobile}
          onClose={() => setShowConfirmationModel(false)}
        />
      )}
    </>
  );
};

export default ResumeMarketingPackage;
